import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Alert, CircularProgress, Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { COLORS } from "../constants";
import { useRechargeApi } from "../hooks/useRechargeApi";
import type { PaymentMethod } from "../types";
import { QUIZ } from "@/theme/quizTokens";

interface Props {
  open: boolean;
  onClose: () => void;
  paymentMethod: PaymentMethod | null;
  onSuccess: (msg: string) => void;
}

export function PaymentUpdateModal({ open, onClose, paymentMethod, onSuccess }: Props) {
  const { call } = useRechargeApi();
  const [loading, setLoading] = useState<"email" | "url" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updateUrl, setUpdateUrl] = useState<string | null>(null);

  const handleSendEmail = async () => {
    if (!paymentMethod) return;
    setLoading("email");
    setError(null);

    try {
      const res = await call("/recharge/payment-method/send-update-email", {
        method: "POST",
        body: { paymentMethodId: paymentMethod.id },
      });
      if (!res.ok) throw new Error(res.error || "Failed to send email");
      onSuccess("Update email sent to customer");
    } catch (err: any) {
      setError(err.message || "Failed to send email");
    } finally {
      setLoading(null);
    }
  };

  const handleGetUrl = async () => {
    if (!paymentMethod) return;
    setLoading("url");
    setError(null);

    try {
      const res = await call<any>("/recharge/payment-method/update-url", {
        method: "POST",
        body: { paymentMethodId: paymentMethod.id },
      });
      if (!res.ok) throw new Error(res.error || "Failed to get update URL");
      setUpdateUrl(res.data?.url || "No URL returned");
    } catch (err: any) {
      setError(err.message || "Failed to get URL");
    } finally {
      setLoading(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontWeight: 800 }}>Payment Update</Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Typography sx={{ mb: 2, fontSize: 14, color: COLORS.textMuted }}>
          {paymentMethod?.payment_details?.brand} **** {paymentMethod?.payment_details?.last4}
        </Typography>
        <Stack spacing={2}>
          <Button variant="outlined" fullWidth onClick={handleSendEmail} disabled={loading !== null} sx={{ textTransform: "none" }}>
            {loading === "email" ? <CircularProgress size={20} /> : "Send Update Email to Customer"}
          </Button>
          <Button variant="outlined" fullWidth onClick={handleGetUrl} disabled={loading !== null} sx={{ textTransform: "none" }}>
            {loading === "url" ? <CircularProgress size={20} /> : "Get Update URL"}
          </Button>
          {updateUrl && (
            <Box sx={{ p: 2, bgcolor: QUIZ.color.cream2, borderRadius: "8px", wordBreak: "break-all" }}>
              <Typography sx={{ fontSize: 12, fontFamily: "monospace" }}>{updateUrl}</Typography>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
